/**
 * Shared, framework-free contract for the experiences search URL. The server
 * page and the two client islands (filter panel, results controls) all read
 * and write the same param names through these helpers so state never drifts.
 *
 * URL params: q, dur, priceMin, priceMax (major currency units), months
 * (YYYY-M csv), dest (slug csv), style (slug csv), facet
 * (`<typeSlug>:<valueSlug>` csv), sort, view, page.
 */

import { exponentOf } from "@tripcart/shared/format";
import {
  formatFacetToken,
  isReservedFilterSlug,
  parseFacetToken,
} from "@tripcart/shared";

export const PRICE_MIN = 0;
/**
 * Sentinel meaning "no upper price limit". A real `Infinity`, not a finite
 * number: the slider's actual ceiling is the catalog's own highest price
 * (`catalogMaxPrice`, computed per-agency and unbounded), so any finite
 * stand-in can be exceeded by a real tour — a $150,000 charter priced above
 * a hardcoded 99999 was being clamped down to exactly that sentinel, which
 * then made buildApiQuery treat it as "no limit" and drop the filter
 * entirely, while the chip still rendered the clamped value as if it were
 * the real cap.
 */
const PRICE_MAX = Number.POSITIVE_INFINITY;
/** Slider ceiling fallback rendered when catalogMaxPrice is not yet available from the API. */
export const PRICE_DISPLAY_MAX = 3000;
export const PRICE_STEP = 10;

/** Results per page. The API paginates server-side; the page renders exactly
 *  this many cards per request (never the whole catalogue). */
export const PAGE_SIZE = 12;

import {
  DURATION_BANDS,
  type DurationBand,
  type DurationBandId,
} from "@tripcart/shared/duration/duration-bands";
import { TOUR_SEARCH_PARAMS } from "@tripcart/shared";

export { DURATION_BANDS };
export type { DurationBand, DurationBandId };

export const MONTH_LABELS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

export type SortOption =
  | "recommended"
  | "price-asc"
  | "price-desc"
  | "duration-asc"
  | "newest";

export const SORT_OPTIONS: { value: SortOption; label: string }[] = [
  { value: "recommended", label: "Recommended" },
  { value: "price-asc", label: "Price: Low to High" },
  { value: "price-desc", label: "Price: High to Low" },
  { value: "duration-asc", label: "Shortest First" },
  { value: "newest", label: "Newest" },
];

const SORT_VALUES = new Set(SORT_OPTIONS.map((s) => s.value));

export type ParamGetter = (key: string) => string | undefined;

export interface ExperienceSearchState {
  q: string;
  dur: string | null;
  priceMin: number;
  priceMax: number;
  months: string[];
  dest: string[];
  style: string[];
  /**
   * The operator's CUSTOM filters only, keyed by filter slug — `{ difficulty:
   * ["easy", "hard"] }`. The five built-in and taxonomy dimensions keep their
   * own params above; see `parseFacets` for why they are kept out of here.
   */
  facets: Record<string, string[]>;
  sort: SortOption;
  view: "grid" | "list";
  page: number;
}

export function splitCsv(value: string | undefined | null): string[] {
  return value
    ? value
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean)
    : [];
}

/**
 * `?facet=difficulty:easy,difficulty:hard,language:nepali` → grouped by filter
 * slug. One CSV param rather than repeated entries, matching how `dest`,
 * `style` and `months` already work — `ParamGetter` reads a single value per
 * key, and the server page and both client islands share it.
 *
 * Two kinds of token are dropped rather than passed on:
 *
 *  - **Malformed ones.** traveler-api's query schema 400s the WHOLE request on
 *    a single bad token, so one hand-edited character in the address bar would
 *    take the tours page down instead of losing one filter.
 *  - **Reserved slugs.** duration / price / date / destination / activity each
 *    own a dedicated param, and `list-public-experiences.service.ts` lets
 *    `destinationSlugs` OVERWRITE `facets.destination` — carrying one dimension
 *    both ways lets a traveler's pick silently vanish.
 */
function parseFacets(raw: string | undefined | null): Record<string, string[]> {
  const out: Record<string, string[]> = {};
  for (const token of splitCsv(raw)) {
    const parsed = parseFacetToken(token);
    if (!parsed) continue;
    if (isReservedFilterSlug(parsed.typeSlug)) continue;
    const bucket = (out[parsed.typeSlug] ??= []);
    if (!bucket.includes(parsed.valueSlug)) bucket.push(parsed.valueSlug);
  }
  return out;
}

function clampInt(
  raw: string | undefined,
  fallback: number,
  min: number,
  max: number,
): number {
  const n = Number.parseInt(raw ?? "", 10);
  if (Number.isNaN(n)) return fallback;
  // `max` is `Infinity` for the price filter (no finite ceiling — see
  // PRICE_MAX above), so a hand-edited URL param like `priceMax=1e21` would
  // otherwise pass through unclamped: `buildApiQuery`'s
  // `Math.round(n * minorFactor)` then overflows into exponential notation
  // ("1e+23"), sent to the API as a literal, malformed query value. Every
  // real price is far below this ceiling, so it never clips a genuine value.
  return Math.min(Number.MAX_SAFE_INTEGER, Math.min(max, Math.max(min, n)));
}

/**
 * The four filters a Tour Search block submits are read by the block's own
 * parameter names (`TOUR_SEARCH_PARAMS`), so renaming one here cannot quietly
 * empty every search the block sends.
 */
export function parseState(get: ParamGetter): ExperienceSearchState {
  const durRaw = get(TOUR_SEARCH_PARAMS.duration);
  const dur = DURATION_BANDS.some((b) => b.id === durRaw) ? durRaw! : null;
  const sortRaw = get("sort");
  const sort = (
    SORT_VALUES.has(sortRaw as SortOption) ? sortRaw : "recommended"
  ) as SortOption;
  const view = get("view") === "grid" ? "grid" : "list";
  return {
    q: (get(TOUR_SEARCH_PARAMS.keyword) ?? "").trim(),
    dur,
    priceMin: clampInt(get("priceMin"), PRICE_MIN, PRICE_MIN, PRICE_MAX),
    priceMax: clampInt(get("priceMax"), PRICE_MAX, PRICE_MIN, PRICE_MAX),
    months: splitCsv(get("months")).filter((m) => /^\d{4}-\d{1,2}$/.test(m)),
    dest: splitCsv(get(TOUR_SEARCH_PARAMS.destination)),
    style: splitCsv(get(TOUR_SEARCH_PARAMS.activity)),
    facets: parseFacets(get("facet")),
    sort,
    view,
    page: Math.max(1, clampInt(get("page"), 1, 1, 100000)),
  };
}

/** True when any results-narrowing filter is active (search, duration, price,
 *  months, destination, or style). Drives whether the "Clear all" affordance
 *  shows — `sort`/`view`/`page` are navigation state, not filters, so a fresh
 *  visit with no filters applied never shows a "Clear all". */
export function hasActiveFilters(state: ExperienceSearchState): boolean {
  return (
    state.q !== "" ||
    state.dur !== null ||
    state.priceMin > PRICE_MIN ||
    state.priceMax < PRICE_MAX ||
    state.months.length > 0 ||
    state.dest.length > 0 ||
    state.style.length > 0 ||
    Object.keys(state.facets).length > 0
  );
}

/** Distinct calendar month numbers (1–12) across the selected `YYYY-M`
 *  entries, sorted. Drives the year-agnostic `months` API param (OR semantics)
 *  so a scattered pick like Jan + Aug matches "January OR August", not the
 *  whole Jan→Aug span. The accompanying date range (below) bounds the years. */
export function monthsToMonthNumbers(months: string[]): number[] {
  const set = new Set<number>();
  for (const m of months) {
    const mo = Number(m.split("-")[1]);
    if (Number.isInteger(mo) && mo >= 0 && mo <= 11) set.add(mo + 1);
  }
  return [...set].sort((a, b) => a - b);
}

/** Earliest first-of-month and latest end-of-month across the selected
 *  months, as YYYY-MM-DD. A contiguous approximation of a multi-month pick. */
function monthsToDateRange(months: string[]): {
  dateFrom?: string;
  dateTo?: string;
} {
  if (months.length === 0) return {};
  const parsed = months
    .map((m) => {
      const [y, mo] = m.split("-");
      return { year: Number(y), month: Number(mo) };
    })
    .filter((p) => p.year > 0 && p.month >= 0 && p.month <= 11)
    .sort((a, b) => a.year - b.year || a.month - b.month);
  if (parsed.length === 0) return {};
  const first = parsed[0]!;
  const last = parsed[parsed.length - 1]!;
  const pad = (n: number) => String(n).padStart(2, "0");
  const lastDay = new Date(last.year, last.month + 1, 0).getDate();
  return {
    dateFrom: `${first.year}-${pad(first.month + 1)}-01`,
    dateTo: `${last.year}-${pad(last.month + 1)}-${pad(lastDay)}`,
  };
}

/** Server-side: build the traveler-api query string. ALL filters, sort, and
 *  pagination are applied by the API — the page renders exactly the returned
 *  page (no in-server filtering/slicing). Multi-select dest/style are
 *  comma-joined (OR within a dimension). */
export function buildApiQuery(
  state: ExperienceSearchState,
  currencyCode: string,
): URLSearchParams {
  // The slider works in MAJOR units (what the labels show); the API wants MINOR
  // units. The factor is the currency's own exponent — 100 for USD/NPR, 1 for
  // JPY (exp-0), 1000 for KWD (exp-3) — never a hardcoded 100 (TRV-M4).
  const minorFactor = 10 ** exponentOf(currencyCode);
  const qs = new URLSearchParams({
    page: String(state.page),
    perPage: String(PAGE_SIZE),
  });
  if (state.sort !== "recommended") qs.set("sort", state.sort);
  if (state.dest.length) qs.set("destination", state.dest.join(","));
  if (state.style.length) qs.set("activity", state.style.join(","));
  if (state.q) qs.set("q", state.q.slice(0, 200));
  if (state.priceMin > PRICE_MIN)
    qs.set("priceMin", String(Math.round(state.priceMin * minorFactor)));
  if (state.priceMax < PRICE_MAX)
    qs.set("priceMax", String(Math.round(state.priceMax * minorFactor)));
  const band = DURATION_BANDS.find((b) => b.id === state.dur);
  if (band?.minMinutes !== undefined)
    qs.set("durationMinMinutes", String(band.minMinutes));
  if (band?.maxMinutes !== undefined)
    qs.set("durationMaxMinutes", String(band.maxMinutes));
  // `append`, not `set`: repeated entries for one filter are how the API reads
  // "easy OR hard" (storefront-experiences.controller.ts accumulates them).
  for (const [typeSlug, valueSlugs] of Object.entries(state.facets)) {
    for (const valueSlug of valueSlugs) {
      qs.append("facet", formatFacetToken(typeSlug, valueSlug));
    }
  }
  const monthNums = monthsToMonthNumbers(state.months);
  if (monthNums.length) qs.set("months", monthNums.join(","));
  const { dateFrom, dateTo } = monthsToDateRange(state.months);
  if (dateFrom) qs.set("dateFrom", dateFrom);
  if (dateTo) qs.set("dateTo", dateTo);
  return qs;
}
